const DEFAULT_REUSE_WINDOW_MS = 1000;

function parseUrl(url) {
  try {
    return new URL(url, 'http://localhost');
  } catch (ex) {
    return null;
  }
}

function isRefreshRequest(url) {
  const parsedUrl = parseUrl(url);
  return (
    parsedUrl?.pathname.endsWith('/jwt/access') &&
    parsedUrl.searchParams.has('refresh_token')
  );
}

function isAccessTokenRequest(url) {
  const parsedUrl = parseUrl(url);
  return Boolean(
    parsedUrl && parsedUrl.searchParams.has('token') && !isRefreshRequest(url),
  );
}

function createTokenRefreshHandler({
  req,
  autoLogin,
  logout,
  getUser,
  redirect,
  handleRetryResult = (value) => value,
  telemetry = () => {},
  reuseWindowMs = DEFAULT_REUSE_WINDOW_MS,
  now = Date.now,
}) {
  let refreshPromise = null;
  let refreshSettledAt = null;
  let concurrentWaiters = 0;
  let retriedRequestCount = 0;

  function getRefreshPromise(isAdmin) {
    if (
      refreshPromise &&
      refreshSettledAt !== null &&
      now() - refreshSettledAt > reuseWindowMs
    ) {
      refreshPromise = null;
      refreshSettledAt = null;
    }

    if (refreshPromise) {
      concurrentWaiters += 1;
      telemetry('token_refresh_concurrent_waiter', {
        concurrentWaiters,
        admin: isAdmin,
      });
      return refreshPromise;
    }

    telemetry('token_refresh_attempt', {admin: isAdmin});
    refreshPromise = (async () => {
      try {
        const refreshed = await autoLogin({admin: isAdmin});
        if (!refreshed) {
          throw new Error('Token refresh returned no session');
        }
        telemetry('token_refresh_success', {admin: isAdmin});
        return true;
      } catch (error) {
        telemetry('token_refresh_failure', {
          admin: isAdmin,
          status: error?.status,
        });
        try {
          await logout(isAdmin);
        } finally {
          redirect(isAdmin ? '/admin' : '/');
        }
        throw error;
      } finally {
        refreshSettledAt = now();
        concurrentWaiters = 0;
      }
    })();

    return refreshPromise;
  }

  return async function onTokenError(url, payload, resp) {
    if (isRefreshRequest(url) && [401, 410].includes(resp.status)) {
      telemetry('token_refresh_loop_detection', {status: resp.status});
      return;
    }

    if (resp.status !== 410 || !isAccessTokenRequest(url)) {
      return;
    }

    const groups = getUser()?.grp?.split(':') || [];
    const isAdmin = groups.includes('admin');

    try {
      await getRefreshPromise(isAdmin);
    } catch (error) {
      throw resp;
    }

    const token = getUser()?.token;
    if (typeof token !== 'string' || !token) {
      throw resp;
    }
    const retryUrl = parseUrl(url);
    retryUrl.searchParams.set('token', token);
    // Preserve absolute/relative paths and hashes, including encoded parameter names.
    const urlWithFreshToken = url.replace(/\?[^#]*/, () => retryUrl.search);

    retriedRequestCount += 1;
    telemetry('token_refresh_retried_request', {
      retriedRequestCount,
      admin: isAdmin,
    });
    const retryResult = await req(urlWithFreshToken, payload, {
      ignoreOnErrorHook: true,
    });
    return handleRetryResult(retryResult);
  };
}

export {createTokenRefreshHandler, isAccessTokenRequest, isRefreshRequest};
